import { isAuthenticatedHomeRoute } from './nativeApp';

export type MessengerReturnTarget = {
  pathname: string;
  state?: Record<string, unknown>;
};

type MessengerHistory = {
  length: number;
  entries?: Array<{ pathname?: string }>;
  index?: number;
  goBack: () => void;
  push: (target: MessengerReturnTarget) => void;
  replace: (target: MessengerReturnTarget) => void;
};

const normalizePathname = (pathname?: string): string => (
  (pathname || '').replace(/^\/+|\/+$/g, '')
);

/**
 * Prefer an explicit owning route over incidental MemoryRouter history.
 *
 * A direct chat link is mounted above the public `landing` entry, so blindly
 * popping history returns an authenticated user to signup. Replacing the chat
 * entry with its declared owner preserves authentication and avoids leaving the
 * messenger in the Back stack. Ordinary in-app chat opens may carry a shallow-
 * stack fallback target, but do not opt into that deep-link preference and
 * therefore continue to use their real previous route.
 */
export function navigateBackFromMessenger(
  history: MessengerHistory,
  returnTo: MessengerReturnTarget | undefined,
  fallback: MessengerReturnTarget,
  preferReturnToOnBack = false,
): void {
  if (preferReturnToOnBack) {
    const destination = returnTo && isAuthenticatedHomeRoute(returnTo.pathname)
      ? returnTo
      : fallback;
    const previousEntry = typeof history.index === 'number' && history.index > 0
      ? history.entries?.[history.index - 1]
      : undefined;
    if (
      previousEntry &&
      normalizePathname(previousEntry.pathname) === normalizePathname(destination.pathname)
    ) {
      history.goBack();
      return;
    }
    history.replace(destination);
    return;
  }

  if (history.length > 1) {
    history.goBack();
    return;
  }

  history.push(returnTo || fallback);
}
